import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { LayoutGrid, LayoutList, Search, Table2 } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  listBandActionRecipe,
  listBandBarRecipe,
  listBandCountRecipe,
  listBandRecipe,
  listColumnRecipe,
  listColumnRowBodyRecipe,
  listColumnRowLeadRecipe,
  listColumnRowLinkRecipe,
  listColumnRowTrailRecipe,
  listFilterTriggerRecipe,
  listFootRecipe,
  listToolbarRecipe,
  listToolbarSearchRecipe,
  type ListBandBarRecipeProps,
  type ListColumnRecipeProps,
  type ListColumnRowLeadRecipeProps,
  type ListFootRecipeProps,
} from '../recipes/list.recipe';
import { Button } from './Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu';
import { Icon, type IconComponent } from './Icon';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import { ListRow } from './List';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select';
import { Text } from './Text';
import { ToggleGroup, ToggleGroupItem } from './ToggleGroup';

/*
 * THE GROUPED LIST COLUMN
 *
 * The pieces of the 400-wide list beside a record: the toolbar over it, the
 * band over each group, the rows (ListColumnRow), and the strip under them.
 * Each piece carries the column's inset, so the column is flush and a
 * selected row can colour its full width. The same pieces are the phone's
 * list, so a screen built from them reads the same in the driver app and on
 * the dashboard at phone width.
 */

export interface ListColumnProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ListColumnRecipeProps {}

/**
 * The grouped list's toolbar, bands and rows, held together.
 *
 *   <MobileScreen>
 *     <ListColumn bleed>
 *       <ListToolbar … />
 *       <ListBand label="At lot" count={5} tone="warning" />
 *       <ListColumnRow lead="IMP-014" trail="385 days">2016 BMW M235i</ListColumnRow>
 *     </ListColumn>
 *   </MobileScreen>
 *
 * `bleed` on a phone screen, whose gutter the pieces already carry. Beside a
 * record, ListDetailLayout is the frame instead, and the column is flush.
 */
export const ListColumn = React.forwardRef<HTMLDivElement, ListColumnProps>(
  function ListColumn({ bleed, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(listColumnRecipe({ bleed }), className)}
        {...props}
      />
    );
  }
);

export interface ListBandProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    ListBandBarRecipeProps {
  label: React.ReactNode;
  count: React.ReactNode;
  /**
   * A control at the far end of the band. The reports list groups rows and
   * has nothing to put here; the checklists list is one band and hangs its
   * Add on it, rather than a second toolbar above a list of five rows.
   */
  action?: React.ReactNode;
}

/**
 * The band over a group of rows: what the group is, and how many are in it.
 *
 *   <ListBand label="Issues found" count={3} tone="destructive" />
 *
 * `tone` follows the badge tone of what the group is, so a red band sits
 * over red badges. A group that is not a severity — a truck type, a driver —
 * is `neutral`, so the reds and greens keep meaning something.
 */
export const ListBand = React.forwardRef<HTMLDivElement, ListBandProps>(
  function ListBand({ label, count, tone, action, className, ...props }, ref) {
    return (
      <div ref={ref} className={cn(listBandRecipe(), className)} {...props}>
        <div className={listBandBarRecipe({ tone })}>
          <Text variant="label" truncate>
            {label}
          </Text>
          <Text variant="label" className={listBandCountRecipe()}>
            {count}
          </Text>
          {action ? (
            <span className={listBandActionRecipe()}>{action}</span>
          ) : null}
        </div>
      </div>
    );
  }
);

/** A string gets the row's type; anything richer brings its own. */
function cellText(content: React.ReactNode, strong = false) {
  if (typeof content !== 'string' && typeof content !== 'number')
    return content;
  return (
    <Text
      as="span"
      variant={strong ? 'body-lg-strong' : 'body-lg'}
      align="left"
      truncate
    >
      {content}
    </Text>
  );
}

export interface ListColumnRowProps {
  /** The fixed first column: an impound number, a truck. */
  lead: React.ReactNode;
  /** `icon` for a lead that is only a glyph. */
  leadWidth?: ListColumnRowLeadRecipeProps['width'];
  /** The middle column, which stretches: the vehicle, who filed it. */
  children: React.ReactNode;
  /** The fixed last column: days at the lot, when it was filed. */
  trail?: React.ReactNode;
  /** The record open beside the list. */
  selected?: boolean;
  /** Picks the row. The row is a button. */
  onClick?: () => void;
  /**
   * Navigates instead: the router's link, empty, which the row fills —
   * `link={<Link to={href} />}`. The library has no router of its own.
   */
  link?: React.ReactElement;
  /** A control beside a link row, inside the same band. Not on a button row. */
  action?: React.ReactNode;
  'aria-label'?: string;
}

/**
 * One row of a grouped list: a fixed lead, whatever tells the rows apart,
 * and a fixed trail, at 16px on a 44-high band.
 *
 *   <ListColumnRow lead="IMP-014" trail="385 days" selected onClick={open}>
 *     2016 BMW M235i
 *   </ListColumnRow>
 *
 * A button when it has an onClick, so a keyboard reaches every row. With
 * `link` it is a link, and an `action` sits beside it rather than inside it:
 * a button inside a button is invalid markup and a keyboard never reaches
 * the inner one. A link is also what the checklist builder's unsaved-changes
 * guard listens for, so a row that navigates asks before it throws an edit
 * away.
 *
 * The dashboard's inspections and trucks columns drew this from two rows of
 * their own at 13px, three pixels under the design, and the phone had a
 * taller row of its own for the same lists.
 */
export const ListColumnRow = React.forwardRef<HTMLElement, ListColumnRowProps>(
  function ListColumnRow(
    {
      lead,
      leadWidth,
      children,
      trail,
      selected,
      onClick,
      link,
      action,
      'aria-label': ariaLabel,
    },
    ref
  ) {
    const current = selected ? 'true' : undefined;
    const content = (
      <>
        <span className={listColumnRowLeadRecipe({ width: leadWidth })}>
          {cellText(lead, true)}
        </span>
        <span className={listColumnRowBodyRecipe()}>{cellText(children)}</span>
        {trail !== undefined && trail !== null ? (
          <span className={listColumnRowTrailRecipe()}>{cellText(trail)}</span>
        ) : null}
      </>
    );

    if (link) {
      return (
        <ListRow ref={ref} shape="band" interactive selected={selected}>
          <Slot
            className={listColumnRowLinkRecipe()}
            aria-current={current}
            aria-label={ariaLabel}
          >
            {React.cloneElement(link, undefined, content)}
          </Slot>
          {action}
        </ListRow>
      );
    }

    return (
      <ListRow
        ref={ref}
        as="button"
        shape="band"
        interactive
        selected={selected}
        onClick={onClick}
        aria-current={current}
        aria-label={ariaLabel}
      >
        {content}
      </ListRow>
    );
  }
);

export interface ListGroup<Row> {
  key: string;
  label: React.ReactNode;
  /** In this group on every page. The rows on this page, without it. */
  count?: number;
  tone?: ListBandProps['tone'];
  rows: Row[];
}

export interface ListGroupsProps<Row> {
  /** The groups on this page, in the server's order. */
  groups: ListGroup<Row>[];
  /** Every group there is, from the server's `meta.groups`. */
  allGroups?: { count: number }[];
  /** This group's rows. */
  children: (rows: Row[]) => React.ReactNode;
}

/**
 * A list's groups, each under its ListBand.
 *
 *   <ListGroups groups={bands} allGroups={meta?.groups}>
 *     {(rows) => rows.map(cardFor)}
 *   </ListGroups>
 *
 * A band tells groups apart, so one group has none: the rows stand on their
 * own. A page can show one group of several, which keeps its band, so that
 * is counted from `allGroups` where the server sends them. Eight screens
 * wrote this loop.
 */
export function ListGroups<Row>({
  groups,
  allGroups,
  children,
}: ListGroupsProps<Row>) {
  const several =
    (allGroups?.filter((group) => group.count > 0).length ?? groups.length) > 1;
  return (
    <Stack gap={0}>
      {groups.map((group) => (
        <Stack key={group.key} as="section" gap={0}>
          {several ? (
            <ListBand
              label={group.label}
              count={group.count || group.rows.length}
              tone={group.tone}
            />
          ) : null}
          {children(group.rows)}
        </Stack>
      ))}
    </Stack>
  );
}

export interface ListFootProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ListFootRecipeProps {}

/**
 * The strip under a list's rows, at the column's inset — a link that reveals
 * the archived rows, say. `RangePager` is this with a pager in it.
 */
export const ListFoot = React.forwardRef<HTMLDivElement, ListFootProps>(
  function ListFoot({ density, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(listFootRecipe({ density }), className)}
        {...props}
      />
    );
  }
);

export interface RangePagerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** One-based. */
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * "Showing 1 to 100 of 250" with Previous, a menu of every page, and Next,
 * under a list that pages. The menu is how someone goes straight to a page:
 * a row of numbers would not fit under the 400-wide list, and could only
 * reach the pages near this one.
 *
 *   <RangePager page={meta.pageNo} pageSize={meta.pageSize}
 *     total={meta.totalCount} totalPages={pageCount(meta)}
 *     onPageChange={setPage} />
 *
 * `meta` and `pageCount` are the API's, from @relay-towing/api.
 */
export const RangePager = React.forwardRef<HTMLDivElement, RangePagerProps>(
  function RangePager(
    { page, pageSize, total, totalPages, onPageChange, ...props },
    ref
  ) {
    return (
      <ListFoot ref={ref} {...props}>
        <Text variant="caption" tone="muted">
          Showing {(page - 1) * pageSize + 1} to{' '}
          {Math.min(page * pageSize, total)} of {total}
        </Text>
        <Inline gap={2}>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Select
            value={String(page)}
            onValueChange={(next) => onPageChange(Number(next))}
          >
            <SelectTrigger size="sm" fullWidth={false} aria-label="Page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: totalPages }, (_, index) => (
                <SelectItem key={index} value={String(index + 1)}>
                  {index + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </Inline>
      </ListFoot>
    );
  }
);

/**
 * How the left column is drawn: grouped rows, the wide table, or a grid of
 * cards for a list that is told apart by its pictures (auction lots).
 */
export type ListMode = 'list' | 'table' | 'grid';

export interface ListToolbarProps {
  mode: ListMode;
  onModeChange: (mode: ListMode) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /** What the box says before anyone types, e.g. "Search by truck or name". */
  searchPlaceholder: string;
  /** What a screen reader hears on the box. */
  searchLabel: string;
  /** Grouping, Filters: whatever sits beside the switch. */
  children?: React.ReactNode;
  /**
   * The dashboard's toolbar (Figma 25:3422): across the page above the list
   * and the record, each control in a hairline box. Its
   * filters are `ListFilter`s. The phone keeps the plain row, flush with the
   * column.
   */
  framed?: boolean;
  /**
   * Offer a Grid beside List and Table, for a list whose rows are told apart
   * by a picture. Like the table, the grid takes the page's width, so a card
   * picked in it opens beside the list (`useListView`).
   */
  grid?: boolean;
}

export interface ListSearchProps {
  search: string;
  onSearchChange: (search: string) => void;
  /** What the box says before anyone types, e.g. "Search by truck or name". */
  searchPlaceholder: string;
  /** What a screen reader hears on the box. */
  searchLabel: string;
}

/**
 * A list's search box, always open: it was behind a magnifier, and people
 * did not find the search. Escape clears it. The value belongs to the page,
 * because one list searches what it has loaded and another asks the server.
 *
 * ListToolbar draws it, except where the dashboard's list sits beside a
 * record: there ListDetailLayout's `search` holds it above the rows, so it is
 * as wide as the list and the record keeps the room beside it.
 */
export function ListSearch({
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
}: ListSearchProps) {
  return (
    <div className={listToolbarSearchRecipe()}>
      <Input
        size="sm"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onSearchChange('');
        }}
        placeholder={searchPlaceholder}
        aria-label={searchLabel}
        leadingIcon={<Search />}
      />
    </div>
  );
}

/**
 * The row above a grouped list: the List / Table switch, the page's own
 * buttons (`Button size="sm"`), and the search box under them.
 *
 *   <ListToolbar mode={mode} onModeChange={setMode} search={search}
 *     onSearchChange={setSearch} searchPlaceholder="Search by truck or name"
 *     searchLabel="Search the reports already loaded">
 *     <Button variant="ghost" size="sm"><Filter />Filters</Button>
 *   </ListToolbar>
 */
export function ListToolbar({
  mode,
  onModeChange,
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  children,
  framed = false,
  grid = false,
}: ListToolbarProps) {
  // Framed, List view has the record beside the rows, so the page hands the
  // box to ListDetailLayout. Everywhere else the list has the row to itself.
  const searchInList = framed && mode === 'list';

  return (
    <Stack gap={framed ? 0 : 3}>
      <div className={listToolbarRecipe({ framed })}>
        <ToggleGroup
          type="single"
          size={framed ? 'toolbar' : 'compact'}
          variant={framed ? 'framed' : 'plain'}
          bordered={false}
          gap={framed ? 2 : 1}
          value={mode}
          onValueChange={(next) => next && onModeChange(next as ListMode)}
        >
          {/* List first, as the design draws it: the list is the view a
              column opens on, and the table is the one you switch to. */}
          <ToggleGroupItem value="list">
            <LayoutList />
            List
          </ToggleGroupItem>
          <ToggleGroupItem value="table">
            <Table2 />
            Table
          </ToggleGroupItem>
          {grid && (
            <ToggleGroupItem value="grid">
              <LayoutGrid />
              Grid
            </ToggleGroupItem>
          )}
        </ToggleGroup>

        <Inline gap={framed ? 2 : 1} align="center" wrap>
          {children}
        </Inline>
      </div>

      {searchInList ? null : (
        <ListSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          searchLabel={searchLabel}
        />
      )}
    </Stack>
  );
}

export interface ListFilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ListFilterProps {
  /** The 16px glyph: Phosphor, as the dashboard's design draws them. */
  icon: IconComponent;
  /** What is being filtered: "Status", "Account". The menu's heading too. */
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly ListFilterOption[];
  /**
   * The choice that leaves the list as it was: "All statuses", or Sort's
   * Newest. While it is chosen the button says what it filters; once
   * another is, it says that one, washed, so a narrowed list says so.
   */
  resting: string;
}

/**
 * One filter in the framed ListToolbar (Figma 25:3437): a hairline box with
 * a glyph and a word, opening a menu of choices.
 *
 *   <ListToolbar framed …>
 *     <ListFilter icon={Broadcast} label="Status" value={status}
 *       onValueChange={setStatus} options={STATUSES} resting="ALL" />
 *   </ListToolbar>
 *
 * The dashboard's impounds filtered through four labelled selects stacked
 * above the list, which cost the column a third of its height for controls
 * set once a visit.
 */
export function ListFilter({
  icon,
  label,
  value,
  onValueChange,
  options,
  resting,
}: ListFilterProps) {
  const chosen = options.find((option) => option.value === value);
  const set = value !== resting && !!chosen;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* design-tokens-ignore: owned by ListFilter */}
        <button
          type="button"
          className={listFilterTriggerRecipe({ active: set })}
          aria-label={set ? `${label}: ${chosen.label}` : label}
        >
          <Icon icon={icon} size="sm" />
          <Text as="span" variant="inherit" truncate>
            {set ? chosen.label : label}
          </Text>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
